"use client";

import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { Alert, Snackbar } from "@mui/material";

const TTSContext = createContext(null);

export const TTSProvider = ({ children }) => {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isGeneratingAudio, setIsGeneratingAudio] = useState(false);
  const [currentTextId, setCurrentTextId] = useState(null);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [speechError, setSpeechError] = useState("");
  const audioRef = useRef(null);
  const speechRequestRef = useRef(null);
  const audioUrlRef = useRef(null);

  useEffect(() => {
    setSpeechSupported(typeof window !== "undefined" && "Audio" in window);
  }, []);

  const stopCurrent = useCallback(() => {
    speechRequestRef.current?.abort();
    speechRequestRef.current = null;
    if (audioRef.current) {
      audioRef.current.onended = null;
      audioRef.current.onerror = null;
      audioRef.current.pause();
      audioRef.current.removeAttribute("src");
      audioRef.current = null;
    }
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }
    setIsSpeaking(false);
    setCurrentTextId(null);
    setIsGeneratingAudio(false);
  }, []);

  const speak = async (text, textId, autoPlay = false) => {
    if (!speechSupported || !text) return;

    if (isSpeaking && currentTextId === textId) {
      stopCurrent();
      setSpeechError("");
      return;
    }

    stopCurrent();
    setSpeechError("");
    const controller = new AbortController();
    speechRequestRef.current = controller;

    try {
      setIsGeneratingAudio(true);
      setCurrentTextId(textId);

      const cleanText = text
        .replace(/\*\*/g, "")
        .replace(/\*/g, "")
        .replace(/#{1,6}\s/g, "")
        .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
        .replace(/\n\n/g, ". ")
        .replace(/\n/g, " ")
        .trim();

      const response = await fetch("/api/support/text-to-speech", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ text: cleanText }),
        signal: AbortSignal.any([
          controller.signal,
          AbortSignal.timeout(40_000),
        ]),
      });

      if (!response.ok) {
        const details = response.headers
          .get("Content-Type")
          ?.includes("application/json")
          ? (await response.json()).error
          : response.statusText;
        throw new Error(
          `Speech request failed (${response.status}): ${details || "Unknown server error"}`,
        );
      }

      const audioBlob = await response.blob();
      if (controller.signal.aborted) return;
      const audioUrl = URL.createObjectURL(audioBlob);
      audioUrlRef.current = audioUrl;

      if (!audioRef.current) {
        audioRef.current = new Audio();
      }

      audioRef.current.playsInline = true;
      audioRef.current.src = audioUrl;
      audioRef.current.volume = 1.0;

      audioRef.current.onended = () => {
        if (!controller.signal.aborted) stopCurrent();
      };

      audioRef.current.onerror = () => {
        if (controller.signal.aborted) return;
        console.error("Speech audio could not be played");
        stopCurrent();
        setSpeechError("Audio could not be played. Please try Read aloud again.");
      };

      await audioRef.current.play();
      if (controller.signal.aborted) return;
      setIsSpeaking(true);
      setIsGeneratingAudio(false);
    } catch (error) {
      if (controller.signal.aborted) return;
      console.error("Text-to-speech error:", error);
      stopCurrent();
      setSpeechError(
        error.name === "NotAllowedError"
          ? "Your browser blocked automatic audio. Tap Read aloud to play the answer."
          : "Speech is unavailable right now. Please try Read aloud again.",
      );
    }
  };

  useEffect(() => {
    return () => {
      speechRequestRef.current?.abort();
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
      }
      if (audioRef.current) {
        audioRef.current.onended = null;
        audioRef.current.onerror = null;
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  return (
    <TTSContext.Provider
      value={{
        speak,
        stopSpeaking: stopCurrent,
        isSpeaking,
        isGeneratingAudio,
        speechSupported,
        currentTextId,
      }}
    >
      {children}
      <Snackbar
        open={Boolean(speechError)}
        autoHideDuration={8000}
        onClose={(_, reason) => {
          if (reason !== "clickaway") setSpeechError("");
        }}
      >
        <Alert severity="warning" onClose={() => setSpeechError("")}>
          {speechError}
        </Alert>
      </Snackbar>
    </TTSContext.Provider>
  );
};

export const useTTSContext = () => {
  const context = useContext(TTSContext);
  if (!context) {
    throw new Error("useTTSContext must be used within TTSProvider");
  }
  return context;
};
